import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
    FaFire,
    FaMapMarkerAlt,
    FaPhoneAlt,
    FaClipboardList,
    FaSatelliteDish,
    FaChevronDown,
    FaChevronUp,
    FaArrowLeft,
    FaRoad,
    FaSchool,
    FaExclamationTriangle,
    FaFileAlt,
} from 'react-icons/fa'
import './FdemData.css'

// Assets
import gmaps from './assets/gmaps-logo.png'
import fdemLogo from './assets/FDEM LOGO_HD.png'

const LOCAL_MODE = false

function apiUrl(path: string): string {
    return LOCAL_MODE ? `http://localhost:8000${path}` : path
}

function credentials(): RequestCredentials {
    return LOCAL_MODE ? 'include' : 'same-origin'
}

// ---------------------------------------------------------------------------
// Types - mirrors FloridaIncidentReportSerializer's output
// ---------------------------------------------------------------------------

// What changed on the report as of the email that created a given update
// row, captured relative to whatever the report looked like immediately
// BEFORE that email was applied. Mirrors FloridaIncidentUpdate.
// previous_values in models.py:
//   - main_information: only the labels that changed, each mapped to its
//     OLD value (labels not present here didn't change on this email).
//   - contact_information / worksheets: the WHOLE previous list, only
//     present at all if something in that list changed - never diffed
//     field-by-field, since list shape itself can change (entries added
//     or removed) between emails.
// Absent entirely ({} server-side) when this update row is a report's very
// first one, or when this email didn't change one of these sections.
interface PreviousValues {
    main_information?: Record<string, unknown>
    contact_information?: FdemContact[]
    worksheets?: FdemWorksheet[]
}

interface FdemUpdate {
    id: number
    update_text: string
    date_et_raw: string
    occurred_at: string | null
    user: string | null
    email_title?: string | null
    previous_values?: PreviousValues | null
}

interface FdemWorksheet {
    type: string | null
    worksheet_name: string | null
    fields: Record<string, string>
}

interface FdemNotificationLogEntry {
    recipients: string
    subject: string
    date_et: string
    user: string
}

interface FdemContact {
    reporting_type?: string
    detail?: string
    name?: string
    represents?: string
    phone?: string
}

interface FdemReport {
    id: number
    report_number: string
    fire_code: string
    status: string
    severity: string
    fire_name: string
    situation_types: string
    affected_counties: string
    acres_burned: string | number | null
    reported_to_swo_at: string | null
    incident_occurred_at: string | null
    latitude: string
    longitude: string
    contact_information: FdemContact[]
    worksheets: FdemWorksheet[]
    notification_log: FdemNotificationLogEntry[]
    updates: FdemUpdate[]
    updated_at: string
    main_information?: Record<string, unknown>
    email_title?: string | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso: string | null): string {
    if (!iso) return '\u2014'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

function worksheetIcon(type: string | null) {
    const t = (type || '').toLowerCase()
    if (t.includes('road')) return <FaRoad aria-hidden />
    if (t.includes('school')) return <FaSchool aria-hidden />
    if (t.includes('fire')) return <FaFire aria-hidden />
    return <FaExclamationTriangle aria-hidden />
}

function googleMapsUrl(lat: string, lon: string): string | null {
    if (!lat || !lon) return null
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`
}

// The "Map" field in main_information is free text like:
//   "Map Coordinates\n\nLat: ...\n\nLong: ... [X]  <https://floridadisaster.maps.arcgis.com/...>"
// - the actual link is FDEM's own ArcGIS incident viewer, wrapped in angle
// brackets by whatever converted the original HTML link to plain text.
function extractFdemMapUrl(mapField: unknown): string | null {
    if (typeof mapField !== 'string') return null
    const match = mapField.match(/<(https?:\/\/[^>]+)>/)
    return match ? match[1] : null
}

function stringField(value: unknown): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value : null
}

// Parses date strings like "6/16/2026 4:21:00 PM" (the same format both
// updates' date_et_raw and notification_log's date_et use) into a
// comparable timestamp. Unparseable input defaults to -Infinity rather
// than NaN, so a malformed date is always treated as "before" any cutoff
// (included) instead of silently dropped - erring toward showing data
// rather than hiding it on a parsing edge case.
function parseEtTimestamp(raw: string | null | undefined): number {
    if (!raw) return -Infinity
    const t = new Date(raw).getTime()
    return Number.isNaN(t) ? -Infinity : t
}

// Strips the "[X]  <url>" link-artifact tail off the Map field's display
// text, since the raw view renders that link as a proper clickable line
// instead of leaving the bracket/angle-bracket syntax visible.
function cleanMapText(raw: string): string {
    return raw.replace(/\s*\[X\]\s*<[^>]+>/, '').trim()
}

// Fields inserted by the parser that aren't part of the original email -
// excluded from the raw view specifically since that view's whole point is
// to look like what FDEM actually sent.
const SYNTHETIC_MAIN_INFO_SUFFIXES = ['(UTC)']

function isSyntheticMainInfoKey(key: string): boolean {
    return SYNTHETIC_MAIN_INFO_SUFFIXES.some(suffix => key.endsWith(suffix))
}

// Canonical field order for the "Main Information" section, matching the
// order FDEM's own emails use. main_information's actual key order should
// already match this (it's built that way by the parser), but ordering
// explicitly here guarantees it regardless, and gracefully handles any
// report missing a field or carrying an unrecognized one.
const MAIN_INFO_ORDER = [
    'Report #',
    'Status',
    'Reported to SWO on (Time in ET)',
    'Severity',
    'Description',
    'This situation involves',
    'Assistance requested?',
    'Facility Name or Description',
    'Incident Location',
    'Map',
    'Initial Report',
    'Injuries',
    'Fatalities (Unconfirmed by State Medical Examiner)',
    'Environmental impact',
    'DEP Callback Requested',
    'Select Contact',
    'Incident Occurred (Time in ET)',
    'Affected Counties',
]

function orderedMainInfoEntries(mainInfo: Record<string, unknown>): [string, unknown][] {
    const remaining = new Map(
        Object.entries(mainInfo).filter(([key]) => !isSyntheticMainInfoKey(key))
    )
    const ordered: [string, unknown][] = []

    for (const key of MAIN_INFO_ORDER) {
        if (remaining.has(key)) {
            ordered.push([key, remaining.get(key)])
            remaining.delete(key)
        }
    }
    // Anything left over (a variant field name we haven't canonicalized,
    // or a genuinely new field) still shows up - just appended at the end
    // rather than silently dropped.
    for (const entry of remaining) {
        ordered.push(entry)
    }
    return ordered
}

// A worksheet's field list can run from 2 fields to 12+, and cards sit
// side by side in a grid - without this, one long worksheet forces every
// card in its row to match its height, leaving the shorter ones mostly
// empty. Measuring actual rendered height (rather than guessing from
// field count, since values vary wildly in length too) means only
// genuinely-tall cards get truncated.
const WORKSHEET_COLLAPSE_HEIGHT = 260 // px

const WorksheetCard: React.FC<{ worksheet: FdemWorksheet }> = ({ worksheet }) => {
    const listRef = useRef<HTMLDListElement>(null)
    const [expanded, setExpanded] = useState(false)
    const [overflowing, setOverflowing] = useState(false)

    useLayoutEffect(() => {
        const el = listRef.current
        if (el) {
            setOverflowing(el.scrollHeight > WORKSHEET_COLLAPSE_HEIGHT + 8)
        }
    }, [worksheet])

    return (
        <div className="fdem-card fdem-worksheet">
            <h3 className="fdem-worksheet-title">
                {worksheetIcon(worksheet.type)} {worksheet.type || 'Worksheet'}
            </h3>
            {worksheet.worksheet_name && (
                <p className="fdem-worksheet-name">{worksheet.worksheet_name}</p>
            )}
            <dl
                ref={listRef}
                className={`fdem-field-list ${overflowing && !expanded ? 'fdem-field-list--collapsed' : ''}`}
            >
                {Object.entries(worksheet.fields).map(([label, value]) => (
                    <React.Fragment key={label}>
                        <dt>{label}</dt>
                        <dd>{value || '\u2014'}</dd>
                    </React.Fragment>
                ))}
            </dl>
            {overflowing && (
                <button
                    type="button"
                    className="fdem-worksheet-toggle"
                    onClick={() => setExpanded(e => !e)}
                    aria-expanded={expanded}
                >
                    {expanded ? <FaChevronUp aria-hidden /> : <FaChevronDown aria-hidden />}
                    {expanded ? 'Show less' : 'Load more'}
                </button>
            )}
        </div>
    )
}

// A Yes/No incident-safety flag from main_information (Injuries, Fatalities,
// Environmental impact, Assistance requested?). Rendered as a small badge
// so these are scannable at a glance rather than buried in the raw JSON -
// "Yes" is flagged in the amber/red status color since it's the answer
// that actually needs attention; "No" is neutral/green.
const FlagBadge: React.FC<{ label: string; value: unknown }> = ({ label, value }) => {
    const v = stringField(value)
    if (v === null) return null
    const isYes = v.trim().toLowerCase() === 'yes'
    return (
        <div className={`fdem-flag ${isYes ? 'fdem-flag--yes' : 'fdem-flag--no'}`}>
            <span className="fdem-flag-label">{label}</span>
            <span className="fdem-flag-value">{v}</span>
        </div>
    )
}

// ---------------------------------------------------------------------------
// Historical snapshot reconstruction
//
// report.main_information / contact_information / worksheets always hold
// the CURRENT (latest) state - there is no per-update historical copy of
// those sections stored anywhere. Instead, each update row carries
// previous_values: whatever changed on the report as a result of the
// email that created that row, captured as "what it looked like right
// before". To find out how the report looked at any earlier point in
// time, start from the current data and walk BACKWARD through every
// update NEWER than that point, applying each one's previous_values on
// top - since report.updates is ordered newest-first (see
// FloridaIncidentUpdate.Meta.ordering), that's simply every update whose
// array index is smaller than the point of interest.
// ---------------------------------------------------------------------------

interface HistoricalSnapshot {
    mainInformation: Record<string, unknown>
    contactInformation: FdemContact[]
    worksheets: FdemWorksheet[]
}

// Reconstructs the report's main_information/contact_information/
// worksheets as they stood immediately after report.updates[uptoIndex]
// was processed. Pass uptoIndex = -1 to reconstruct the state BEFORE any
// update at all (i.e. the Initial slip) - that applies every update's
// previous_values on top of the current data.
function reconstructSnapshot(report: FdemReport, uptoIndex: number): HistoricalSnapshot {
    const mainInformation: Record<string, unknown> = { ...(report.main_information ?? {}) }
    let contactInformation = report.contact_information
    let worksheets = report.worksheets

    const newerUpdates = report.updates.slice(0, uptoIndex === -1 ? report.updates.length : uptoIndex)

    // Applied in the array's existing newest-to-oldest order, which is
    // exactly the order that correctly walks the snapshot backward one
    // email at a time.
    for (const u of newerUpdates) {
        const prev = u.previous_values
        if (!prev) continue
        if (prev.main_information) {
            for (const [key, value] of Object.entries(prev.main_information)) {
                mainInformation[key] = value
            }
        }
        if (prev.contact_information) {
            contactInformation = prev.contact_information
        }
        if (prev.worksheets) {
            worksheets = prev.worksheets
        }
    }

    return { mainInformation, contactInformation, worksheets }
}

// ---------------------------------------------------------------------------
// Raw email view - a faithful reconstruction of the original FDEM email
// layout, built entirely from data already stored on the report (nothing
// here needs a different API call). Mirrors the source email's own section
// order: Most Recent Updates -> Main Information -> Contact Information ->
// Worksheets -> Notification Log.
//
// The report is presented as a list of collapsible "slips" - one per
// distinct source email - each rendering the complete report but with its
// own "Most Recent Updates" table truncated to only what was known as of
// that email, AND its Main Information / Contact Information / Worksheets
// reconstructed to how they actually looked at that point in time (via
// reconstructSnapshot), rather than always showing the report's current
// state. The oldest slip is always the Initial report itself
// (visibleUpdates = []), regardless of whether the incident has since
// picked up any updates at all.
// ---------------------------------------------------------------------------

interface SlipDescriptor {
    key: string
    emailTitle: string | null
    dateEtRaw: string
    visibleUpdates: FdemUpdate[]
    isLatestSlip: boolean
    // Index into report.updates whose "as of right after this email" state
    // this slip should show, per reconstructSnapshot's contract. -1 for
    // the Initial slip (state before any update at all).
    reconstructIndex: number
    // The DB id of this slip's representative FloridaIncidentUpdate row -
    // shown in the header as a quick cross-reference back to the raw
    // record (e.g. when comparing against the admin or the API directly).
    // null for the Initial slip, which isn't backed by an update row.
    updateId: number | null
}

// One email's "Most Recent Updates" table is cumulative - it can (and
// often does) introduce several update rows at once that weren't in the
// DB yet, all sharing the same email_title since they all came from that
// one send. Those shouldn't each get their own clickable slip - a slip
// represents "what this report looked like as of one email," not "one
// update row." Grouping by email_title and keeping only the most recent
// row per group (report.updates is already sorted newest-first, so the
// first row encountered for a given title is automatically that group's
// most recent) collapses them down to one slip per actual email received,
// while visibleUpdates still uses each representative row's ORIGINAL
// index into the full array - so the expanded content still shows every
// historical update up to that point, grouping only affects which rows
// get their own header. reconstructIndex uses that same original index,
// so Main Information/Contacts/Worksheets are reconstructed consistently
// with whichever updates are visible in that slip.
function buildSlipDescriptors(report: FdemReport): SlipDescriptor[] {
    const slips: SlipDescriptor[] = []
    const seenTitles = new Set<string>()

    report.updates.forEach((u, i) => {
        const hasTitle = typeof u.email_title === 'string' && u.email_title.trim() !== ''
        // Rows with no email_title (not yet backfilled) never group with
        // each other - each gets its own slip rather than risking merging
        // unrelated rows together under a shared "untitled" bucket.
        const groupKey = hasTitle ? (u.email_title as string) : `__no_title_${u.id}`
        if (seenTitles.has(groupKey)) return
        seenTitles.add(groupKey)

        slips.push({
            key: String(u.id),
            emailTitle: hasTitle ? (u.email_title as string) : null,
            dateEtRaw: u.date_et_raw,
            visibleUpdates: report.updates.slice(i),
            isLatestSlip: i === 0,
            reconstructIndex: i,
            updateId: u.id,
        })
    })

    // The Initial report is always the oldest slip, whether or not the
    // incident has picked up any updates since.
    const initialDateRaw =
        stringField(report.main_information?.['Reported to SWO on (Time in ET)']) ??
        formatDateTime(report.reported_to_swo_at)

    slips.push({
        key: 'initial',
        emailTitle: report.email_title ?? null,
        dateEtRaw: initialDateRaw,
        visibleUpdates: [],
        isLatestSlip: report.updates.length === 0,
        reconstructIndex: -1,
        updateId: null,
    })

    return slips
}

const RawReportBody: React.FC<{
    report: FdemReport
    snapshot: HistoricalSnapshot
    visibleUpdates: FdemUpdate[]
    fdemMapUrl: string | null
    isLatestSlip: boolean
}> = ({ report, snapshot, visibleUpdates, fdemMapUrl, isLatestSlip }) => {
    const mainInfoEntries = orderedMainInfoEntries(snapshot.mainInformation)

    const hasStructuredContacts = snapshot.contactInformation.some(c => c.name || c.represents || c.phone)

    // Same "point in time" logic as the updates table itself: this slip's
    // cutoff is its own newest visible update (visibleUpdates[0], since
    // visibleUpdates is already sliced to that update and everything
    // older). Notification log entries are filtered to only those
    // STRICTLY before that update's timestamp - an entry at the exact
    // same moment as the update doesn't count as having happened before
    // it, and entries from later updates that hadn't happened yet at this
    // point in the report's history are excluded too.
    //
    // The Initial slip has no visibleUpdates of its own to anchor on
    // (visibleUpdates is always [] there), but it still needs a cutoff -
    // otherwise it falls through to "show everything," which is wrong:
    // the Initial email predates every update, so any notification log
    // entry tied to the FIRST real update (or later) hadn't happened yet
    // at that point. report.updates is ordered newest-first, so the
    // OLDEST update - and therefore the correct cutoff for the Initial
    // slip - is the last element in that array.
    //
    // If the report has no updates at all, there's no update timestamp to
    // fall back on either - in that case, use the incident's own
    // "Incident Occurred (Time in ET)" as the cutoff instead of showing
    // every log entry unfiltered. This is read off `snapshot`, not
    // `report`, so it reflects the reconstructed Initial-slip value if
    // previous_values ever changed that field (unlikely in practice, but
    // consistent with how every other field on this slip is sourced).
    const oldestUpdateDateEtRaw = report.updates.length > 0
        ? report.updates[report.updates.length - 1].date_et_raw
        : null
    const incidentOccurredDateEtRaw = stringField(snapshot.mainInformation['Incident Occurred (Time in ET)'])
    const cutoffTimestamp = visibleUpdates.length > 0
        ? parseEtTimestamp(visibleUpdates[0].date_et_raw)
        : oldestUpdateDateEtRaw !== null
            ? parseEtTimestamp(oldestUpdateDateEtRaw)
            : incidentOccurredDateEtRaw !== null
                ? parseEtTimestamp(incidentOccurredDateEtRaw)
                : null

    // Note: unlike Main Information/Contacts/Worksheets, the notification
    // log itself is NOT reconstructed via previous_values - it's purely
    // additive (old entries never change or disappear), so filtering the
    // CURRENT list down to entries before this slip's cutoff is already
    // equivalent to how it looked at that point in time.
    const visibleNotificationLog = cutoffTimestamp === null
        ? report.notification_log
        : report.notification_log.filter(
              entry => parseEtTimestamp(entry.date_et) < cutoffTimestamp
          )

    return (
        <div className="fdem-raw">
            <h2 className="fdem-raw-title">State Watch Office Incident Report</h2>

            {visibleUpdates.length > 0 && (
                <section className="fdem-raw-section">
                    <h3 className="fdem-raw-heading">Most Recent Updates</h3>
                    <table className="fdem-raw-table">
                        <thead>
                            <tr>
                                <th>Update</th>
                                <th>Date (Time in ET)</th>
                                <th>User</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleUpdates.map((u, i) => (
                                <tr key={u.id ?? i}>
                                    <td>{u.update_text}</td>
                                    <td className="fdem-raw-nowrap">{u.date_et_raw}</td>
                                    <td className="fdem-raw-nowrap">{u.user}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}

            <section className="fdem-raw-section">
                <h3 className="fdem-raw-heading">Main Information</h3>
                <table className="fdem-raw-table fdem-raw-table--kv">
                    <tbody>
                        {mainInfoEntries.map(([label, value]) => {
                            if (label === 'Status' && report.status === 'Closed' && !isLatestSlip && stringField(value) === 'Closed') {
                                // Fallback only, for update rows recorded
                                // before previous_values existed (so
                                // reconstruction has nothing to overlay
                                // and snapshot.mainInformation.Status still
                                // shows the report's final "Closed" value
                                // here). Any point in time before the
                                // final update must have still been open.
                                // Rows WITH previous_values already produce
                                // the correct historical status above and
                                // never hit this branch, since their
                                // reconstructed value won't equal "Closed"
                                // unless it genuinely was at that point.
                                return (
                                    <tr key={label}>
                                        <td className="fdem-raw-label">{label}</td>
                                        <td className="fdem-raw-value">Open</td>
                                    </tr>
                                )
                            }
                            if (label === 'Incident Location' && value && typeof value === 'object') {
                                const loc = value as Record<string, string>
                                const text = [
                                    `Lat: ${loc.lat ?? ''}`,
                                    `Long: ${loc.long ?? ''}`,
                                    `Address: ${loc.address ?? ''}`,
                                    `City: ${loc.city ?? ''}`,
                                    `Zip: ${loc.zip ?? ''}`,
                                ].join('\n')
                                return (
                                    <tr key={label}>
                                        <td className="fdem-raw-label">{label}</td>
                                        <td className="fdem-raw-value">{text}</td>
                                    </tr>
                                )
                            }
                            if (label === 'Map' && typeof value === 'string') {
                                return (
                                    <tr key={label}>
                                        <td className="fdem-raw-label">{label}</td>
                                        <td className="fdem-raw-value">
                                            {cleanMapText(value)}
                                            {fdemMapUrl && (
                                                <>
                                                    {'\n'}
                                                    <a href={fdemMapUrl} target="_blank" rel="noreferrer" className="fdem-raw-inline-link">
                                                        View map →
                                                    </a>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                )
                            }
                            return (
                                <tr key={label}>
                                    <td className="fdem-raw-label">{label}</td>
                                    <td className="fdem-raw-value">{stringField(value) ?? ''}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </section>

            <section className="fdem-raw-section">
                <h3 className="fdem-raw-heading">Contact Information</h3>
                {hasStructuredContacts ? (
                    <table className="fdem-raw-table">
                        <thead>
                            <tr>
                                <th>Reporting Type</th>
                                <th>Name</th>
                                <th>Represents</th>
                                <th>Phone</th>
                            </tr>
                        </thead>
                        <tbody>
                            {snapshot.contactInformation.map((c, i) => (
                                <tr key={i}>
                                    <td>{c.reporting_type ?? ''}</td>
                                    <td>{c.name ?? ''}</td>
                                    <td>{c.represents ?? ''}</td>
                                    <td className="fdem-raw-nowrap">{c.phone ?? ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <table className="fdem-raw-table fdem-raw-table--kv">
                        <tbody>
                            {snapshot.contactInformation.map((c, i) => (
                                <tr key={i}>
                                    <td className="fdem-raw-label">{c.reporting_type ?? 'Contact'}</td>
                                    <td className="fdem-raw-value">{c.detail ?? ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                <table className="fdem-raw-table fdem-raw-table--kv fdem-raw-table--blank">
                    <tbody>
                        <tr><td className="fdem-raw-label">Address :</td><td /></tr>
                        <tr><td className="fdem-raw-label">City:</td><td /></tr>
                        <tr><td className="fdem-raw-label">State:</td><td /></tr>
                        <tr><td className="fdem-raw-label">Zip code:</td><td /></tr>
                        <tr><td className="fdem-raw-label">Email:</td><td /></tr>
                    </tbody>
                </table>
            </section>

            {snapshot.worksheets.length > 0 && (
                <section className="fdem-raw-section">
                    <h3 className="fdem-raw-heading">Worksheets</h3>
                    {snapshot.worksheets.map((w, i) => (
                        <div key={i} className="fdem-raw-worksheet">
                            <h4 className="fdem-raw-subheading">{w.type || 'Worksheet'}</h4>
                            <table className="fdem-raw-table fdem-raw-table--kv">
                                <tbody>
                                    <tr>
                                        <td className="fdem-raw-label">Worksheet Name</td>
                                        <td className="fdem-raw-value">{w.worksheet_name ?? ''}</td>
                                    </tr>
                                    {Object.entries(w.fields).map(([label, value]) => (
                                        <tr key={label}>
                                            <td className="fdem-raw-label">{label}</td>
                                            <td className="fdem-raw-value">{value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </section>
            )}

            {visibleNotificationLog.length > 0 && (
                <section className="fdem-raw-section">
                    <h3 className="fdem-raw-heading">Notification Log</h3>
                    <table className="fdem-raw-table">
                        <thead>
                            <tr>
                                <th>Contacts</th>
                                <th>Subject</th>
                                <th>Date (Time in ET)</th>
                                <th>User</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleNotificationLog.map((entry, i) => (
                                <tr key={i}>
                                    <td className="fdem-raw-nowrap">{entry.recipients}</td>
                                    <td>{entry.subject}</td>
                                    <td className="fdem-raw-nowrap">{entry.date_et}</td>
                                    <td className="fdem-raw-nowrap">{entry.user}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}

            <p className="fdem-raw-disclaimer">
                The State Watch Office values your feedback. Under Florida law, correspondence with the
                Florida Division of Emergency Management concerning agency business that is neither
                confidential nor exempt pursuant to Florida Statutes is a public record and will be made
                available to the public upon request.
            </p>
        </div>
    )
}

const RawEmailView: React.FC<{ report: FdemReport; fdemMapUrl: string | null }> = ({ report, fdemMapUrl }) => {
    // Most recent slip (index 0) expanded by default - that's the current
    // full state of the report, which is what most people want to see
    // first. Index 0 is always the newest available slip regardless of
    // whether the report has any updates at all, since the Initial slip
    // is only ever appended at the END of the list.
    const [expandedIndex, setExpandedIndex] = useState<number>(0)
    const slips = buildSlipDescriptors(report)

    return (
        <div className="fdem-slip-list">
            {slips.map((slip, i) => {
                const isExpanded = expandedIndex === i
                // Only reconstructed when actually needed for rendering -
                // cheap either way (a handful of small object copies per
                // slip), but no reason to do it for collapsed slips that
                // never mount their body.
                const snapshot = isExpanded ? reconstructSnapshot(report, slip.reconstructIndex) : null
                return (
                    <div className="fdem-slip" key={slip.key}>
                        <button
                            type="button"
                            className="fdem-slip-header"
                            onClick={() => setExpandedIndex(isExpanded ? -1 : i)}
                            aria-expanded={isExpanded}
                        >
                            <span className="fdem-slip-chevron">
                                {isExpanded ? <FaChevronUp aria-hidden /> : <FaChevronDown aria-hidden />}
                            </span>
                            {slip.updateId !== null && <span className="fdem-slip-user">ID: {slip.updateId}</span>}
                            <span className="fdem-slip-date fdem-mono">{slip.dateEtRaw}</span>
                            <span className="fdem-slip-preview">{slip.emailTitle || 'Untitled email'}</span>
                        </button>
                        {isExpanded && snapshot && (
                            <div className="fdem-slip-body">
                                <RawReportBody
                                    report={report}
                                    snapshot={snapshot}
                                    visibleUpdates={slip.visibleUpdates}
                                    fdemMapUrl={fdemMapUrl}
                                    isLatestSlip={slip.isLatestSlip}
                                />
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FdemData: React.FC = () => {
    const { report_number } = useParams<{ report_number: string }>()
    const [report, setReport] = useState<FdemReport | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [logOpen, setLogOpen] = useState(false)
    const [viewMode] = useState<'raw' | 'organized'>('raw')

    useEffect(() => {
        if (!report_number) return

        let cancelled = false
        setLoading(true)
        setError(null)

        const load = async () => {
            try {
                const res = await fetch(
                    apiUrl(`/api/florida-incidents/?search=${encodeURIComponent(report_number)}`),
                    { method: 'GET', credentials: credentials() }
                )
                if (!res.ok) {
                    throw new Error(res.status === 403 || res.status === 401
                        ? 'You need to be signed in to view this report.'
                        : `Request failed (${res.status})`)
                }
                const data = await res.json()
                const list: FdemReport[] = Array.isArray(data) ? data : data.results ?? []
                const match =
                    list.find(r => r.report_number === report_number) ?? list[0] ?? null

                if (cancelled) return
                if (!match) {
                    setError(`No report found for ${report_number}.`)
                } else {
                    setReport(match)
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load report.')
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        load()
        return () => {
            cancelled = true
        }
    }, [report_number])

    if (loading) {
        return (
            <div className="fdem-page">
                <div className="fdem-status-screen">
                    <FaSatelliteDish className="fdem-status-icon fdem-pulse" aria-hidden />
                    <p>Pulling incident {report_number}\u2026</p>
                </div>
            </div>
        )
    }

    if (error || !report) {
        return (
            <div className="fdem-page">
                <div className="fdem-status-screen">
                    <FaExclamationTriangle className="fdem-status-icon fdem-status-icon--error" aria-hidden />
                    <p>{error ?? 'This report could not be found.'}</p>
                    <Link to="/" className="fdem-back-link">
                        <FaArrowLeft aria-hidden /> Back
                    </Link>
                </div>
            </div>
        )
    }

    const isOpen = report.status === 'Open'
    const mapUrl = googleMapsUrl(report.latitude, report.longitude)
    const fdemMapUrl = extractFdemMapUrl(report.main_information?.Map)
    const situationTags = report.situation_types
        ? report.situation_types.split(',').map(s => s.trim()).filter(Boolean)
        : []

    const mainInfo = report.main_information ?? {}
    const initialReport = stringField(mainInfo['Initial Report'])

    return (
        <div className="fdem-page">
            <div className="fdem-container">
                {/* This banner is intentionally shown regardless of viewMode -
                    it's a data-provenance disclaimer, not a raw-view detail, so
                    it needs to stay visible even if the organized view is ever
                    re-enabled as the default. */}
                <div className="fdem-source-disclaimer">
                    <FaExclamationTriangle aria-hidden />
                    <p>
                        This data is <strong>not</strong> generated by FNN™ It is captured directly, and
                        unaltered, from incident report emails sent by the Florida State Watch Office
                        (<span className="fdem-mono">swp@em.myflorida.com</span>) to{' '}
                        <span className="fdem-mono">firemonitoring@fireneuralnetwork.com</span>.
                    </p>
                </div>

                {viewMode === 'raw' && (
                    <RawEmailView report={report} fdemMapUrl={fdemMapUrl} />
                )}

                {viewMode === 'organized' && (
                    <>
                        {/* ---------------- Hero ---------------- */}
                        <header className="fdem-hero">
                            <div className="fdem-hero-top">
                                <span className={`fdem-status-pill ${isOpen ? 'fdem-status-pill--open' : 'fdem-status-pill--closed'}`}>
                                    <span className="fdem-status-dot" />
                                    {report.status || 'Unknown'}
                                </span>
                                <span className="fdem-fire-code">{report.fire_code}</span>
                            </div>

                            <h1 className="fdem-fire-name">{report.fire_name}</h1>

                            <div className="fdem-tag-row">
                                {situationTags.map(tag => (
                                    <span className="fdem-tag" key={tag}>{tag}</span>
                                ))}
                            </div>
                        </header>

                        {/* ---------------- Stat strip ---------------- */}
                        <section className="fdem-stat-strip">
                            <div className="fdem-stat">
                                <span className="fdem-stat-label">Report #</span>
                                <span className="fdem-stat-value fdem-mono">{report.report_number}</span>
                            </div>
                            <div className="fdem-stat">
                                <span className="fdem-stat-label">Severity</span>
                                <span className="fdem-stat-value">{report.severity || '\u2014'}</span>
                            </div>
                            <div className="fdem-stat">
                                <span className="fdem-stat-label">Acres burned</span>
                                <span className="fdem-stat-value fdem-mono">{report.acres_burned ?? '\u2014'}</span>
                            </div>
                            <div className="fdem-stat">
                                <span className="fdem-stat-label">Counties</span>
                                <span className="fdem-stat-value">{report.affected_counties || '\u2014'}</span>
                            </div>
                            <div className="fdem-stat">
                                <span className="fdem-stat-label">Occurred</span>
                                <span className="fdem-stat-value fdem-mono">{formatDateTime(report.incident_occurred_at)}</span>
                            </div>
                            <div className="fdem-stat">
                                <span className="fdem-stat-label">Reported to SWO</span>
                                <span className="fdem-stat-value fdem-mono">{formatDateTime(report.reported_to_swo_at)}</span>
                            </div>
                        </section>

                        <div className="fdem-grid">
                            {/* ---------------- Location ---------------- */}
                            <section className="fdem-card">
                                <h2 className="fdem-card-title"><FaMapMarkerAlt aria-hidden /> Location</h2>
                                {report.latitude && report.longitude ? (
                                    <>
                                        <p className="fdem-mono fdem-coords">
                                            {report.latitude}, {report.longitude}
                                        </p>
                                        <div className="fdem-map-links">
                                            {mapUrl && (
                                                <a href={mapUrl} target="_blank" rel="noreferrer" className="fdem-link fdem-map-link">
                                                    <img src={gmaps} alt="" className="fdem-map-icon" />
                                                    Open in Google Maps →
                                                </a>
                                            )}
                                            {fdemMapUrl && (
                                                <a href={fdemMapUrl} target="_blank" rel="noreferrer" className="fdem-link fdem-map-link">
                                                    <img src={fdemLogo} alt="" className="fdem-map-icon" />
                                                    Open in FDEM Map Viewer →
                                                </a>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <p className="fdem-muted">No coordinates on file.</p>
                                )}
                            </section>

                            {/* ---------------- Contacts ---------------- */}
                            <section className="fdem-card">
                                <h2 className="fdem-card-title"><FaPhoneAlt aria-hidden /> Contacts</h2>
                                {report.contact_information.length === 0 && (
                                    <p className="fdem-muted">No contact information on file.</p>
                                )}
                                {report.contact_information.map((c, i) => (
                                    <div className="fdem-contact" key={i}>
                                        <span className="fdem-contact-type">{c.reporting_type || 'Contact'}</span>
                                        {c.detail ? (
                                            <span className="fdem-muted">{c.detail}</span>
                                        ) : (
                                            <>
                                                {c.name && <span>{c.name}</span>}
                                                {c.represents && <span className="fdem-muted">{c.represents}</span>}
                                                {c.phone && <span className="fdem-mono">{c.phone}</span>}
                                            </>
                                        )}
                                    </div>
                                ))}
                            </section>
                        </div>

                        {/* ---------------- Initial Report ---------------- */}
                        {Boolean(initialReport || mainInfo['Injuries'] || mainInfo['Fatalities (Unconfirmed by State Medical Examiner)'] || mainInfo['Environmental impact'] || mainInfo['Assistance requested?']) && (
                            <section className="fdem-card fdem-initial-report">
                                <h2 className="fdem-card-title"><FaFileAlt aria-hidden /> Initial Report</h2>
                                {initialReport && <p className="fdem-initial-report-text">{initialReport}</p>}
                                <div className="fdem-flag-row">
                                    <FlagBadge label="Injuries" value={mainInfo['Injuries']} />
                                    <FlagBadge label="Fatalities" value={mainInfo['Fatalities (Unconfirmed by State Medical Examiner)']} />
                                    <FlagBadge label="Environmental Impact" value={mainInfo['Environmental impact']} />
                                    <FlagBadge label="Assistance Requested" value={mainInfo['Assistance requested?']} />
                                </div>
                            </section>
                        )}

                        {/* ---------------- Worksheets ---------------- */}
                        {report.worksheets.length > 0 && (
                            <section className="fdem-section">
                                <h2 className="fdem-section-title"><FaClipboardList aria-hidden /> Worksheets</h2>
                                <div className="fdem-worksheet-grid">
                                    {report.worksheets.map((w, i) => (
                                        <WorksheetCard worksheet={w} key={i} />
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* ---------------- Dispatch log (updates) ---------------- */}
                        <section className="fdem-section">
                            <h2 className="fdem-section-title"><FaSatelliteDish aria-hidden /> Dispatch Updates</h2>
                            <ol className="fdem-timeline">
                                {report.updates.map((u, i) => (
                                    <li className="fdem-timeline-entry" key={u.id ?? i}>
                                        <span className="fdem-timeline-time fdem-mono">
                                            {formatDateTime(u.occurred_at) !== '\u2014' ? formatDateTime(u.occurred_at) : u.date_et_raw}
                                        </span>
                                        <div className="fdem-timeline-body">
                                            <p>{u.update_text}</p>
                                            {u.user && <span className="fdem-timeline-user">— {u.user}</span>}
                                        </div>
                                    </li>
                                ))}
                                {report.updates.length === 0 && (
                                    <p className="fdem-muted">No updates logged yet.</p>
                                )}
                            </ol>
                        </section>

                        {/* ---------------- Notification log (collapsible) ---------------- */}
                        {report.notification_log.length > 0 && (
                            <section className="fdem-section">
                                <button
                                    type="button"
                                    className="fdem-collapse-toggle"
                                    onClick={() => setLogOpen(o => !o)}
                                    aria-expanded={logOpen}
                                >
                                    {logOpen ? <FaChevronUp aria-hidden /> : <FaChevronDown aria-hidden />}
                                    Notification log ({report.notification_log.length})
                                </button>
                                {logOpen && (
                                    <div className="fdem-card fdem-log-table-wrap">
                                        <table className="fdem-log-table">
                                            <thead>
                                                <tr>
                                                    <th>Subject</th>
                                                    <th>Sent</th>
                                                    <th>User</th>
                                                    <th>Recipients</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {report.notification_log.map((entry, i) => (
                                                    <tr key={i}>
                                                        <td>{entry.subject}</td>
                                                        <td className="fdem-mono">{entry.date_et}</td>
                                                        <td>{entry.user}</td>
                                                        <td className="fdem-mono">{entry.recipients}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>
                        )}

                        <p className="fdem-footer-note fdem-mono">
                            Last synced {formatDateTime(report.updated_at)}
                        </p>
                    </>
                )}
            </div>
        </div>
    )
}

export default FdemData